import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";
import { sanitizeEventTypes } from "../_components/eventTypes";
import type { EventType } from "../_components/eventTypes";
import { matchesQuickView } from "../_components/quickViews";
import type { QuickView } from "../_components/quickViews";
import type { CoordinatorFilterValue } from "../_components/dashboardCoordinators";
import AttentionStrip from "./AttentionStrip";
import ClosingStrip from "./ClosingStrip";
import HighlightedStrip from "./HighlightedStrip";
import ShowTimeline from "./ShowTimeline";
import ShowQuickPanel from "./ShowQuickPanel";
import { useShowBoard } from "./useShowBoard";
import { clientLabel, outlinedIconSx, placeLabel, THEME } from "./boardStyles";
import type { BoardShow } from "./boardTypes";

const SPANS = [
  { days: 14, label: "2 weeks", step: 7 },
  { days: 28, label: "4 weeks", step: 7 },
  { days: 42, label: "6 weeks", step: 7 },
  { days: 91, label: "3 months", step: 28 },
];
const SPAN_KEY = "dashboardOverview.span.v1";
const TIME_FILTERS = ["ongoing", "upcoming", "past"];

function readSpan(): number {
  try {
    const v = Number(localStorage.getItem(SPAN_KEY));
    return SPANS.some((s) => s.days === v) ? v : 28;
  } catch {
    return 28;
  }
}

function saveSpan(days: number) {
  try {
    localStorage.setItem(SPAN_KEY, String(days));
  } catch {
    /* per-browser convenience only */
  }
}

const thisWeek = () => dayjs().startOf("week").format("YYYY-MM-DD");

/** Text search over what the row shows (same box as the table search). */
function matchesSearch(show: BoardShow, q: string): boolean {
  if (!q) return true;
  const hay = [
    show.eventName,
    clientLabel(show),
    show.client?.name,
    show.venue,
    placeLabel(show),
    show.jobNumber ? String(show.jobNumber) : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => hay.includes(term));
}

/** Coordinators column filter: any / all of the selected people. */
function matchesCoordinators(show: BoardShow, filter?: CoordinatorFilterValue | null): boolean {
  const ids = (filter?.selected ?? []).map((c) => c._id);
  if (!ids.length) return true;
  const mine = show.coordinators.map((c) => c._id);
  return filter?.match === "all" ? ids.every((id) => mine.includes(id)) : ids.some((id) => mine.includes(id));
}

/**
 * Status filter, same meaning as on the table: nothing picked = every running,
 * upcoming and past show (no cancelled); Cancelled adds the cancelled ones;
 * picking only Cancelled lists only them.
 */
function matchesStatus(show: BoardShow, statuses: string[]): boolean {
  if (show.lifecycle === "cancelled") return statuses.includes("cancelled");
  if (!statuses.length) return true;
  const time = statuses.filter((s) => TIME_FILTERS.includes(s));
  if (!time.length) return false;
  return time.includes(show.timing);
}

type Props = {
  /** Opens the full show page (or Saved Drafts for a draft). */
  onOpenShow: (show: BoardShow) => void;
  /** Shared dashboard filters (header search + funnel), same state as the table and the calendar. */
  search: string;
  statusFilters: Array<{ value: string }>;
  eventTypes: EventType[];
  coordinatorFilter: CoordinatorFilterValue;
  /** Quick view chips (unconfirmed / upcoming N days / ongoing): filter the timeline. */
  quickView?: QuickView;
  /** "Show duplicates" checkbox: the board also returns the shows marked as duplicate. */
  showDuplicates?: boolean;
  /** Highlighted shows (shared with the table and the calendar). */
  highlightIds?: Set<string>;
  onToggleHighlight?: (show: BoardShow) => void;
  /** Bumps after each saved highlight change: reload the board. */
  highlightVersion?: number;
};

/**
 * Dashboard "Overview": what needs attention first, then every show on one
 * timeline. Readiness and attention come from GET /dashboard/board; the
 * header search and the funnel filter it here (display only).
 * The attention and "To close" strips only follow search and coordinators:
 * they are alarms, so a status or type filter never hides a show that is
 * about to slip or a payment that is late.
 */
export default function DashboardOverview({
  onOpenShow,
  search,
  statusFilters,
  eventTypes,
  coordinatorFilter,
  quickView = null,
  showDuplicates = false,
  highlightIds,
  onToggleHighlight,
  highlightVersion = 0,
}: Props) {
  const compact = useMediaQuery("(max-width: 900px)");

  const [span, setSpan] = useState<number>(readSpan);
  const [from, setFrom] = useState<string>(thisWeek);
  const to = useMemo(() => dayjs(from).add(span - 1, "day").format("YYYY-MM-DD"), [from, span]);
  const step = SPANS.find((s) => s.days === span)?.step ?? 7;
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useShowBoard(from, to, true, showDuplicates);
  // Acting as coordinator the API only returns the shows you are assigned to: say so.
  const mine = data?.scope === "mine";

  useEffect(() => saveSpan(span), [span]);

  // A highlight was saved somewhere (star here, in the table or in the panel): reload.
  const firstVersion = useRef(highlightVersion);
  useEffect(() => {
    if (highlightVersion !== firstVersion.current) refetch();
  }, [highlightVersion, refetch]);

  // "Upcoming, next N days": start the timeline this week and make it long enough.
  useEffect(() => {
    if (quickView?.view !== "upcoming") return;
    const need = quickView.days + 7;
    const fit = SPANS.find((s) => s.days >= need) ?? SPANS[SPANS.length - 1];
    setFrom(thisWeek());
    setSpan((current) => (current >= fit.days ? current : fit.days));
  }, [quickView?.view, quickView?.days]);

  const statuses = useMemo(() => (statusFilters ?? []).map((s) => s.value), [statusFilters]);
  const types = useMemo(() => sanitizeEventTypes(eventTypes), [eventTypes]);

  const attentionShows = useMemo(
    () =>
      (data?.attention ?? []).filter(
        (s) => matchesSearch(s, search) && matchesCoordinators(s, coordinatorFilter),
      ),
    [data, search, coordinatorFilter],
  );

  const closingShows = useMemo(
    () =>
      (data?.closing ?? []).filter(
        (s) => matchesSearch(s, search) && matchesCoordinators(s, coordinatorFilter),
      ),
    [data, search, coordinatorFilter],
  );

  const timelineShows = useMemo(
    () =>
      (data?.shows ?? []).filter(
        (s) =>
          matchesSearch(s, search) &&
          matchesCoordinators(s, coordinatorFilter) &&
          matchesStatus(s, statuses) &&
          (s.lifecycle === "cancelled" || types.includes(s.lifecycle as EventType)) &&
          matchesQuickView(s, quickView) &&
          (showDuplicates || !s.isDuplicate),
      )
      // Highlighted shows first, the rest keep the API order (by start day).
      .sort((a, b) => Number(!!highlightIds?.has(b._id)) - Number(!!highlightIds?.has(a._id))),
    [data, search, coordinatorFilter, statuses, types, quickView, showDuplicates, highlightIds],
  );

  // Starred shows, any date. Unstarring hides the card at once (before the reload).
  const highlightedShows = useMemo(
    () => (data?.highlighted ?? []).filter((s) => !highlightIds || highlightIds.has(s._id)),
    [data, highlightIds],
  );

  const allById = useMemo(() => {
    const m = new Map<string, BoardShow>();
    for (const s of [
      ...(data?.shows ?? []),
      ...(data?.attention ?? []),
      ...(data?.closing ?? []),
      ...(data?.highlighted ?? []),
    ])
      m.set(s._id, s);
    return m;
  }, [data]);
  const openShow = openId ? allById.get(openId) ?? null : null;

  const today = data?.today ?? dayjs().format("YYYY-MM-DD");
  const rangeLabel = `${dayjs(from).format("MMM D")} – ${dayjs(to).format(
    dayjs(from).isSame(dayjs(to), "year") ? "MMM D" : "MMM D, YYYY",
  )}`;

  return (
    <Box
      sx={{
        padding: "0 40px 48px",
        display: "flex",
        flexDirection: "column",
        gap: "28px",
        "@media (max-width: 600px)": { padding: "0 20px 32px" },
      }}
    >
      <Box sx={{ height: 3, mt: "-12px" }}>
        {loading ? <LinearProgress color="secondary" sx={{ height: 3, borderRadius: 2 }} /> : null}
      </Box>

      {error ? (
        <Box
          role="alert"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            padding: "12px 16px",
            borderRadius: "12px",
            border: "1px solid #FD355A",
          }}
        >
          <Typography>{error}</Typography>
          <Button size="small" onClick={refetch}>
            Retry
          </Button>
        </Box>
      ) : null}

      {onToggleHighlight ? (
        <HighlightedStrip
          shows={highlightedShows}
          max={data?.maxHighlights ?? 5}
          onOpen={(s) => setOpenId(s._id)}
          onToggleHighlight={onToggleHighlight}
        />
      ) : null}

      <AttentionStrip
        shows={attentionShows}
        loading={loading && !data}
        mine={mine}
        highlightIds={highlightIds}
        onOpen={(s) => setOpenId(s._id)}
      />

      {data?.canSeeMoney ? (
        <ClosingStrip
          shows={closingShows}
          loading={loading && !data}
          highlightIds={highlightIds}
          onOpen={(s) => setOpenId(s._id)}
        />
      ) : null}

      <Box component="section" aria-label="All shows" sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "14px" }}>
            <Typography variant="h4" component="h2">
              All shows
            </Typography>
            <Typography sx={{ fontSize: "15px", opacity: 0.8 }}>
              {mine ? `${rangeLabel} · only the shows you coordinate` : rangeLabel}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              aria-label="Previous"
              onClick={() => setFrom((f) => dayjs(f).subtract(step, "day").format("YYYY-MM-DD"))}
              sx={outlinedIconSx}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              aria-label="Next"
              onClick={() => setFrom((f) => dayjs(f).add(step, "day").format("YYYY-MM-DD"))}
              sx={outlinedIconSx}
            >
              <ChevronRight />
            </IconButton>
            <Button size="small" onClick={() => setFrom(thisWeek())} sx={{ minWidth: "auto", px: 2, py: "4px" }}>
              Today
            </Button>
            <Select
              size="small"
              value={span}
              onChange={(e) => setSpan(Number(e.target.value))}
              inputProps={{ "aria-label": "Timeline length" }}
              sx={{
                color: THEME.white,
                fontSize: "15px",
                borderRadius: "0.4rem",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: `${THEME.white} !important` },
                "& .MuiSvgIcon-root": { color: THEME.white },
              }}
            >
              {SPANS.map((s) => (
                <MenuItem key={s.days} value={s.days}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <ShowTimeline
          shows={timelineShows}
          from={from}
          to={to}
          today={today}
          compact={compact}
          mine={mine}
          highlightIds={highlightIds}
          onToggleHighlight={onToggleHighlight}
          onOpen={(s) => setOpenId(s._id)}
        />

        {data?.truncated ? (
          <Typography sx={{ fontSize: "14px", color: "#FACE70" }}>
            Too many shows in this window; showing the first ones. Pick a shorter length.
          </Typography>
        ) : null}
      </Box>

      <ShowQuickPanel
        show={openShow}
        onClose={() => setOpenId(null)}
        onOpenShow={onOpenShow}
        highlighted={!!openShow && !!highlightIds?.has(openShow._id)}
        onToggleHighlight={onToggleHighlight}
      />
    </Box>
  );
}
